import { cn } from "@/lib/utils";

interface ToggleOption<T extends string> {
  value: T;
  label: string;
}

interface ToggleGroupProps<T extends string> {
  options: ToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
}

/**
 * Toggle button group for selecting options
 */
export function ToggleGroup<T extends string>({
  options,
  value,
  onChange,
  size = "md",
}: ToggleGroupProps<T>) {
  return (
    <div className="inline-flex rounded-xl bg-muted p-1 gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "font-medium transition-all duration-200 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            size === "sm" ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm",
            value === option.value
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-background/50"
          )}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Yes/No toggle component
 */
interface YesNoToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  size?: "sm" | "md";
}

export function YesNoToggle({ value, onChange, size = "md" }: YesNoToggleProps) {
  return (
    <ToggleGroup
      options={[
        { value: "no", label: "No" },
        { value: "yes", label: "Yes" },
      ]}
      value={value ? "yes" : "no"}
      onChange={(v) => onChange(v === "yes")}
      size={size}
    />
  );
}
